import { createContext, useContext } from 'react';

// Per-open-pedigree instance scope.
//
// A single pedigree tool is a D3/SVG renderer that historically assumed exactly
// ONE chart per document: it stamps fixed element ids (`pedigree-chart`,
// `dot-grid`, `dx-confirmed-stripe`, per-node clip/marker ids), references them
// via document-wide `url(#…)` functional IRIs, and reads/writes selection with
// `document.querySelector('g[data-selection-pid=…]')`. That is safe with one
// chart but collides the moment two are mounted at once (the multi-pedigree-tabs
// keep-mounted workspace) — pids are per-family and reused, so selection, drag,
// legend hover and export bleed across instances.
//
// This context carries the small amount of per-instance scope the render layer
// needs to stay isolated:
//   - `instanceId`  — a stable, DOM-id-safe token unique to this open instance.
//                     Used to namespace every id the SVG both creates AND
//                     references via url(#…). `null` ⇒ single-instance mode, ids
//                     are left un-suffixed so the classic single-chart DOM is
//                     byte-identical to before this change.
//   - `isActive`    — whether this instance is the visible/active tab. Global
//                     `window` keyboard shortcuts (Ctrl+A, arrow nudge,
//                     undo/redo) act only on the active instance. Defaults to
//                     true so a lone chart always responds.
//   - `containerRef`— a ref to the DOM element that encloses this instance's
//                     canvas AND sidebar, so export / selection lookups can be
//                     scoped to `containerRef.current.querySelector(…)` instead
//                     of `document`. `null` ⇒ fall back to `document` (the
//                     single-instance path is unchanged).
//
// Consumers outside a provider get the default value below, which is exactly the
// pre-existing single-chart behaviour. The workspace host supplies a real value
// per open session.
export const PedigreeInstanceContext = createContext({
  instanceId: null,
  isActive: true,
  containerRef: { current: null }
});

export const usePedigreeInstance = () => useContext(PedigreeInstanceContext);

// Namespace a DOM id with the instance token. Returns `base` unchanged when
// there is no instance (single-chart mode) so existing charts serialise/export
// exactly as before. The separator is chosen to never appear in a numeric pid or
// the fixed base ids, and to be valid inside an id and an `url(#…)` reference.
export const nsId = (base, instanceId) =>
  instanceId ? `${base}--i${instanceId}` : base;

// Build a bound namespacer for a render pass: `const iid = makeIid(instanceId)`
// then `iid('dot-grid')` / `iid(d.pid)`. Keeping the same function on both the
// id-creation and the `url(#…)` side guarantees they always agree.
export const makeIid = (instanceId) => (base) => nsId(base, instanceId);
